import { useEffect, useState } from "react";
import {
  CalendarDays,
  Crown,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Store,
  UserRound,
  Users,
  UserSearch,
  VenusAndMars,
} from "lucide-react";
import VisitStatCard from "../components/VisitStatCard";
import VisitTable from "../components/VisitTable";
import GenderPieChart from "../components/GenderPieChart";
import AgeBarChart from "../components/AgeBarChart";
import RepeatVisitorTable from "../components/VisitLog/RepeatVisitorTable";

function buildRepeatVisitors(logs) {
  const map = {};

  logs.forEach((log) => {
    const id = log.ulucu_user_id;
    if (!id) return;

    if (!map[id]) {
      map[id] = {
        ulucu_user_id: id,
        visitor_type: log.visitor_type,
        sex: log.sex,
        age: log.age,
        store_id: log.store_id,
        count: 1,
        first_visit: log.arrive_time_thai,
        last_visit: log.arrive_time_thai,
        arrive_image_url: log.arrive_image_url,
      };
    } else {
      map[id].count += 1;
      map[id].last_visit = log.arrive_time_thai;
    }
  });

  return Object.values(map)
    .filter((visitor) => visitor.count > 1)
    .sort((a, b) => b.count - a.count);
}

const defaultFilters = {
  store_id: "all",
  sex: "all",
  visitor_type: "all",
  from: "",
  to: "",
};

const fieldClass =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10";

export default function VisitLogPage() {
  const [logs, setLogs] = useState([]);
  const [stores, setStores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(defaultFilters);

  const repeatVisitors = buildRepeatVisitors(logs);

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setInitialLoading(true);
    setError("");

    const token = localStorage.getItem("token");
    const params = new URLSearchParams();

    if (filters.store_id !== "all") params.append("store_id", filters.store_id);
    if (filters.sex !== "all") params.append("sex", filters.sex);
    if (filters.visitor_type !== "all") params.append("visitor_type", filters.visitor_type);
    if (filters.from && filters.to) {
      params.append("from", filters.from);
      params.append("to", filters.to);
    }

    try {
      const res = await fetch(`/api/visit-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("ไม่สามารถโหลดข้อมูลได้");

      const json = await res.json();
      setLogs(json.data || []);
      setStores(json.stores || []);
      setSummary(json.summary || {});
      setCharts(json.charts || {});
    } catch (fetchError) {
      setError(fetchError.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
    } finally {
      if (!silent) setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      window.location.href = "/login";
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sse = new EventSource("/api/visit-log-dashboard/stream");
    let debounce;

    sse.onmessage = () => {
      clearTimeout(debounce);
      setIsRefreshing(true);
      debounce = setTimeout(async () => {
        await fetchData({ silent: true });
        setTimeout(() => setIsRefreshing(false), 700);
      }, 300);
    };

    return () => {
      sse.close();
      clearTimeout(debounce);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => setFilters(defaultFilters);

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-5 md:p-8">
        <div className="mx-auto max-w-[1600px] animate-pulse space-y-6">
          <div className="h-20 rounded-2xl bg-slate-200" />
          <div className="h-40 rounded-2xl bg-white" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[1, 2, 3, 4].map((item) => <div key={item} className="h-28 rounded-2xl bg-white" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-teal-700">
              <span className="h-2 w-2 rounded-full bg-teal-500" />
              Visitor analytics
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">บันทึกผู้เข้าใช้บริการ</h1>
            <p className="mt-1 text-sm text-slate-500">ติดตามจำนวนผู้เข้าใช้บริการ พฤติกรรม และข้อมูลประชากรแบบเรียลไทม์</p>
          </div>
          <div className="flex items-center gap-2 self-start rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 sm:self-auto">
            <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} />
            {isRefreshing ? "กำลังอัปเดตข้อมูล" : "ข้อมูลเชื่อมต่อแบบเรียลไทม์"}
          </div>
        </header>

        <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm md:p-5">
          <div className="mb-4 flex items-center gap-2">
            <Search size={18} className="text-teal-600" />
            <h2 className="font-semibold text-slate-800">ค้นหาและกรองข้อมูล</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <label className="space-y-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><Store size={13} /> สาขา</span>
              <select className={fieldClass} value={filters.store_id} onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}>
                <option value="all">ทุกสาขา</option>
                {stores.map((store) => <option key={store.store_id} value={store.store_id}>{store.device_name}</option>)}
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><CalendarDays size={13} /> ตั้งแต่วันที่</span>
              <input type="date" className={fieldClass} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
            </label>
            <label className="space-y-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><CalendarDays size={13} /> ถึงวันที่</span>
              <input type="date" className={fieldClass} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
            </label>
            <label className="space-y-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><VenusAndMars size={13} /> เพศ</span>
              <select className={fieldClass} value={filters.sex} onChange={(e) => setFilters({ ...filters, sex: e.target.value })}>
                <option value="all">ทุกเพศ</option><option value="1">ผู้ชาย</option><option value="0">ผู้หญิง</option>
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><UserSearch size={13} /> ประเภทผู้ใช้</span>
              <select className={fieldClass} value={filters.visitor_type} onChange={(e) => setFilters({ ...filters, visitor_type: e.target.value })}>
                <option value="all">ทุกประเภท</option><option value="unknown">ไม่ทราบ</option><option value="พนักงาน">พนักงาน</option><option value="ลูกค้าทั่วไป">ลูกค้าทั่วไป</option><option value="ลูกค้า VIP">ลูกค้า VIP</option>
              </select>
            </label>
            <div className="flex items-end gap-2">
              <button onClick={() => fetchData()} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700 focus:ring-4 focus:ring-teal-500/20"><Search size={16} /> ค้นหา</button>
              <button onClick={resetFilters} title="ล้างตัวกรอง" className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"><RotateCcw size={16} /></button>
            </div>
          </div>
        </section>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <VisitStatCard title="ผู้เข้าใช้ทั้งหมด" value={summary?.total} icon={Users} />
          <VisitStatCard title="ผู้ชาย" value={summary?.male} color="blue" icon={UserRound} />
          <VisitStatCard title="ผู้หญิง" value={summary?.female} color="pink" icon={UserRound} />
          <VisitStatCard title="ผู้เข้าใช้ซ้ำ" value={repeatVisitors.length} color="emerald" icon={RefreshCw} />
        </section>

        <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <VisitStatCard title="ลูกค้า VIP" value={summary?.vip} color="yellow" icon={Crown} />
          <VisitStatCard title="ลูกค้าทั่วไป" value={summary?.member} color="emerald" icon={Users} />
          <VisitStatCard title="พนักงาน" value={summary?.staff} color="indigo" icon={ShieldCheck} />
          <VisitStatCard title="ไม่ทราบประเภท" value={summary?.unknown} color="gray" icon={UserSearch} />
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="min-w-0"><GenderPieChart data={charts?.gender} /></div>
          <div className="min-w-0"><AgeBarChart data={charts?.age} /></div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div><h2 className="font-semibold text-slate-900">รายการผู้เข้าใช้บริการ</h2><p className="mt-0.5 text-xs text-slate-500">รายละเอียดการตรวจจับล่าสุดตามตัวกรองที่เลือก</p></div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{logs.length.toLocaleString("th-TH")} รายการ</span>
          </div>
          <VisitTable data={logs} />
        </section>

        <RepeatVisitorTable data={repeatVisitors} />
      </div>
    </div>
  );
}
